<template>
    <div class="tutor-theme grid flex flex-column justify-content-center align-items-center w-full ">
        <CustomBreadcrumb class="flex align-self-start p-4 "
            :items="[{ label: 'Profile Settings' }, { label: 'Payout Details' }]" />
        
        <span class="H6-Medium block text-center mt-6">Bank Account Information</span>
        <p class="text-gray-500 text-sm text-center mb-4">Please ensure your bank details are accurate to avoid payment delays.</p>

        <div v-if="isInitialLoading" class="flex justify-content-center align-items-center p-8">
            <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
        </div>

        <form v-else class=" md:col-11 md:p-5 mt-2 pl-4">
            <div class=" w-full col-12 grid ">

                <div class="col-12 md:col-6 md:pr-3">
                    <div class="flex flex-column gap-2 pt-2 relative">
                        <label for="accountName" class="Lable">Account Holder Name</label>
                        <InputText id="accountName" v-model="form.accountName" class="w-full" variant="filled" size="small"
                            placeholder="As it appears on your passbook" @blur="markTouched('accountName')" />
                        <small v-if="errors.accountName" class="text-red-500 text-xs absolute" style="bottom: -22px; left: 2px;">{{ errors.accountName }}</small>
                    </div>
                </div>

                <div class="col-12 md:col-6 md:pr-3">
                    <div class="flex flex-column gap-2 pt-2 relative">
                        <label for="accountNumber" class="Lable">Account Number</label>
                        <InputText id="accountNumber" v-model="form.accountNumber" class="w-full" variant="filled" size="small"
                            @keypress="onlyAllowNumbers" @blur="markTouched('accountNumber')" />
                        <small v-if="errors.accountNumber" class="text-red-500 text-xs absolute" style="bottom: -22px; left: 2px;">{{ errors.accountNumber }}</small>
                    </div>
                </div>

                <div class="col-12 md:col-6 md:pr-3">
                    <div class="flex flex-column gap-2 pt-2 relative">
                        <label for="bankName" class="Lable">Bank Name</label>
                        <Select v-model="form.bank" :options="banks" size="small" variant="filled" optionLabel="name"
                            placeholder="Select your bank" class="w-full" @change="markTouched('bank')" />
                        <small v-if="errors.bank" class="text-red-500 text-xs absolute" style="bottom: -22px; left: 2px;">{{ errors.bank }}</small>
                    </div>
                </div>

                <div class="col-12 md:col-6 md:pr-3">
                    <div class="flex flex-column gap-2 pt-2 relative">
                        <label for="branchName" class="Lable">Branch Name</label>
                        <InputText id="branchName" v-model="form.branchName" class="w-full" variant="filled" size="small"
                             @blur="markTouched('branchName')" />
                        <small v-if="errors.branchName" class="text-red-500 text-xs absolute" style="bottom: -22px; left: 2px;">{{ errors.branchName }}</small>
                    </div>
                </div>

                <div class="col-12 md:col-6 md:pr-3">
                    <div class="flex flex-column gap-2 pt-2 relative">
                        <label for="swiftCode" class="Lable">Branch Code / SWIFT (Optional)</label>
                        <InputText id="swiftCode" v-model="form.swiftCode" class="w-full" variant="filled" size="small" />
                    </div>
                </div>

                <div class="col-12 flex flex-column pt-6 px-2">
                    <div class="flex align-items-center gap-2 mb-4 p-3 border-round surface-100">
                        <i class="pi pi-info-circle text-blue-500"></i>
                        <span class="text-xs text-gray-600">Withdrawals are typically processed within 3-5 business days of the payout date.</span>
                    </div>
                    
                    <div style="max-width: 300px;">
                        <Button 
                            label="Save Payout Details" 
                            class="teal-button w-full md:w-auto" 
                            :disabled="!isFormDirty || isSubmitting"
                            :loading="isSubmitting"
                            @click.prevent="handleSubmit" 
                            size="small" 
                        />
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import CustomBreadcrumb from '@/components/CustomBreadcrumb.vue';
import { Button, InputText, Select } from "primevue";
import { getBankInfo, updateBankInfo } from "@/api/tutor";
import { notifyError, notifySuccess } from "@/utils/notification";


const banks = ref([
    { name: 'Bank of Ceylon', code: 'BOC' },
    { name: 'Commercial Bank', code: 'COMB' },
    { name: 'Hatton National Bank', code: 'HNB' },
    { name: 'Sampath Bank', code: 'SAMP' },
    { name: 'Peoples Bank', code: 'PEOP' }
]);

const form = ref({
    accountName: "",
    accountNumber: "",
    bank: null,
    branchName: "",
    swiftCode: ""
});

const originalForm = ref(null);
const touchedFields = ref(new Set());
const isInitialLoading = ref(true);
const isSubmitting = ref(false);

onMounted(async () => {
    try {
        const response = await getBankInfo();
        if (response.data?.success && response.data?.data) {
            const data = response.data.data;
            
            form.value = {
                accountName: data.accountName || "",
                accountNumber: data.accountNumber || "",
                bank: banks.value.find(b => b.name === data.bankName) || null,
                branchName: data.branchName || "",
                swiftCode: data.swiftCode || ""
            };
        }
        originalForm.value = JSON.parse(JSON.stringify(form.value));
    } catch (error) {
        notifyError("Error", "Could not load bank details.");
    } finally {
        isInitialLoading.value = false;
    }
});


const markTouched = (field) => touchedFields.value.add(field);

const onlyAllowNumbers = (event) => {
    if (!/[0-9]/.test(String.fromCharCode(event.which))) event.preventDefault();
};

const errors = computed(() => {
    const errs = {};
    if (touchedFields.value.has('accountName') && !form.value.accountName) errs.accountName = "Account holder name is required";
    if (touchedFields.value.has('accountNumber') && !form.value.accountNumber) errs.accountNumber = "Account number is required";
    if (touchedFields.value.has('bank') && !form.value.bank) errs.bank = "Bank selection is required";
    if (touchedFields.value.has('branchName') && !form.value.branchName) errs.branchName = "Branch name is required";
    return errs;
});

const isFormDirty = computed(() => {
    if (!originalForm.value) return false;
    return JSON.stringify(form.value) !== JSON.stringify(originalForm.value);
});

const handleSubmit = async () => {
    ['accountName', 'accountNumber', 'bank', 'branchName'].forEach(f => touchedFields.value.add(f));
    
    if (Object.keys(errors.value).length === 0) {
        isSubmitting.value = true;
        
        const payload = {
            accountName: form.value.accountName,
            accountNumber: form.value.accountNumber,
            bankName: form.value.bank.name,
            branchName: form.value.branchName,
            swiftCode: form.value.swiftCode
        };

        try {
            const res = await updateBankInfo(payload);
            if (res.data.success) {
                notifySuccess("Success", "Payout details saved successfully");
                originalForm.value = JSON.parse(JSON.stringify(form.value));
            }
        } catch (error) {
            console.error(error);
            notifyError("Update Failed", error.response?.data?.message || "Please try again later.");
        } finally {
            isSubmitting.value = false;
        }
    }
}
</script>

<style scoped>
.Lable {
    font-weight: 500;
    font-size: 0.9rem;
    color: #4b5563;
}
</style>